/**
 * TimestampEditor — small inline numeric editor for `startSec` / `endSec`.
 * Blur or Enter commits; Escape cancels.
 */

import { memo, useState, useCallback, useEffect } from 'react'

interface TimestampEditorProps {
  blockId: string
  startSec: number
  endSec: number
  onCommit: (startSec: number, endSec: number) => void
  onCancel: () => void
}

export const TimestampEditor = memo(function TimestampEditor({
  blockId,
  startSec,
  endSec,
  onCommit,
  onCancel,
}: TimestampEditorProps) {
  const [start, setStart] = useState(startSec.toFixed(1))
  const [end, setEnd] = useState(endSec.toFixed(1))
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setStart(startSec.toFixed(1))
    setEnd(endSec.toFixed(1))
  }, [startSec, endSec])

  const commit = useCallback(() => {
    const s = parseFloat(start)
    const e = parseFloat(end)
    if (!Number.isFinite(s) || !Number.isFinite(e)) {
      setError('invalid')
      return
    }
    if (s >= e) {
      setError('start < end')
      return
    }
    setError(null)
    onCommit(s, e)
  }, [start, end, onCommit])

  return (
    <span
      className="inline-flex items-center gap-1 bg-surface-2 rounded px-1"
      data-testid={`timestamp-editor-${blockId}`}
    >
      <input
        type="number"
        step={0.1}
        min={0}
        value={start}
        onChange={(e) => setStart(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            commit()
          } else if (e.key === 'Escape') {
            e.preventDefault()
            onCancel()
          }
        }}
        className="w-10 bg-input-bg border border-input-border rounded px-0.5 text-xs font-medium"
        data-testid={`timestamp-editor-${blockId}-start`}
      />
      <span className="text-xs font-medium text-text-muted">→</span>
      <input
        type="number"
        step={0.1}
        min={0}
        value={end}
        onChange={(e) => setEnd(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            commit()
          } else if (e.key === 'Escape') {
            e.preventDefault()
            onCancel()
          }
        }}
        className="w-10 bg-input-bg border border-input-border rounded px-0.5 text-xs font-medium"
        data-testid={`timestamp-editor-${blockId}-end`}
      />
      {error && <span className="text-xxs text-red-400">{error}</span>}
    </span>
  )
})
